// === FIRESTORE RULES PATCH — for ops to apply ===
//
// Add these blocks inside `match /databases/{database}/documents { ... }`
// in firebase/firestore.rules and deploy via `firebase deploy --only firestore:rules`.
//
//   match /analytics_events/{doc} {
//     allow create: if request.resource.data.keys().hasAll(['eventName', 'ts'])
//                    && request.resource.data.eventName is string;
//     allow read, update, delete: if false;
//   }
//   match /email_signups/{doc} {
//     allow create: if request.resource.data.email is string
//                    && request.resource.data.email.matches('.+@.+\\..+');
//     allow read, update, delete: if false;
//   }
//
// =================================================================

import {
  collection,
  doc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import type { FieldValue } from 'firebase/firestore';
import { auth, db, isFirebaseConfigured } from '../firebase/config';

// ─── GA4 bridge ──────────────────────────────────────────────────────
// The gtag.js snippet in index.html creates window.gtag and calls
// gtag('config', 'G-XXXXXXXXXX', { send_page_view: false }). This module
// forwards each tracked event to GA4 in parallel with the Firestore write,
// so the user gets both: a free first-party data store AND the GA4 reporting
// suite (real-time, acquisition, conversions, etc.).
declare global {
  interface Window {
    gtag?: (command: 'event' | 'config' | 'set' | 'js', ...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Forward an event to GA4 via window.gtag if it's available.
 * Silently no-ops if gtag.js hasn't loaded (e.g. CSP blocked, ad-blocker,
 * or the user hasn't pasted their Measurement ID into index.html yet).
 */
function sendToGA4(name: EventName, properties: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return;
  try {
    gtag('event', name, properties);
  } catch {
    /* never let analytics break the app */
  }
}

/**
 * Send a SPA route-change page_view to GA4. Called from trackPageView in
 * addition to the Firestore write, since the gtag.js snippet was configured
 * with `send_page_view: false` to avoid double-counting the initial hit.
 */
function sendPageViewToGA4(path: string, title?: string): void {
  if (typeof window === 'undefined') return;
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return;
  try {
    gtag('event', 'page_view', {
      page_path: path,
      page_title: title ?? (typeof document !== 'undefined' ? document.title : ''),
      page_location: typeof window !== 'undefined' ? window.location.href : '',
    });
  } catch {
    /* swallow */
  }
}

// ─── Types ───────────────────────────────────────────────────────────

export type EventName =
  | 'page_view'
  | 'signup_start'
  | 'signup_complete'
  | 'lesson_preview_open'
  | 'lesson_start'
  | 'lesson_complete'
  | 'question_answered'
  | 'continue_clicked'
  | 'interactive_engaged'
  | 'email_capture'
  | 'email_capture_error'
  | 'share_click'
  | 'cta_click'
  | 'subscription_start';

export interface UtmData {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  referrer?: string;
}

interface QueuedEvent {
  eventName: EventName;
  properties: Record<string, unknown>;
  path: string;
  referrer: string;
  utm: UtmData | null;
  anonId: string;
  sessionId: string;
  userId: string | null;
  ts: FieldValue;
  lang: string;
  viewport: { w: number; h: number };
  ua: string;
}

// ─── Storage keys ────────────────────────────────────────────────────

const ANON_ID_KEY = 'goodatoms_anon_id';
const SESSION_ID_KEY = 'goodatoms_session_id';
const UTM_KEY = 'goodatoms_utm';
const LANG_KEY = 'goodatoms-lang';

// ─── Internal state ──────────────────────────────────────────────────

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;
const FLUSH_INTERVAL_MS = 2000;
const FLUSH_MAX_QUEUE = 10;

// ─── Environment guards ──────────────────────────────────────────────

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

function hasDocument(): boolean {
  return typeof document !== 'undefined';
}

function hasLocalStorage(): boolean {
  if (!hasWindow()) return false;
  try {
    return typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function hasSessionStorage(): boolean {
  if (!hasWindow()) return false;
  try {
    return typeof window.sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

function isDev(): boolean {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

// ─── UUID helper ─────────────────────────────────────────────────────

function uuid(): string {
  if (
    hasWindow() &&
    typeof window.crypto !== 'undefined' &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    return window.crypto.randomUUID();
  }
  // Fallback (non-crypto) — only reached in environments without crypto
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── Identity & UTM ──────────────────────────────────────────────────

/**
 * Returns a persistent UUID stored in localStorage under `goodatoms_anon_id`.
 * Generates and persists one on first call. SSR-safe (returns a transient UUID
 * if storage is unavailable so callers never crash).
 */
export function getAnonId(): string {
  if (!hasLocalStorage()) return uuid();
  try {
    const existing = window.localStorage.getItem(ANON_ID_KEY);
    if (existing) return existing;
    const fresh = uuid();
    window.localStorage.setItem(ANON_ID_KEY, fresh);
    return fresh;
  } catch {
    return uuid();
  }
}

/**
 * Returns a UUID stored in sessionStorage. Lives until the tab closes.
 * SSR-safe.
 */
export function getSessionId(): string {
  if (!hasSessionStorage()) return uuid();
  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const fresh = uuid();
    window.sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  } catch {
    return uuid();
  }
}

/**
 * Captures UTM parameters from the current URL and persists them to
 * localStorage as JSON under `goodatoms_utm`. If the current URL has no UTM
 * params, returns the previously stored UTM (does not overwrite). Returns
 * null if nothing is known.
 */
export function captureUtm(): UtmData | null {
  if (!hasWindow() || !hasLocalStorage()) return null;

  let parsed: URLSearchParams | null = null;
  try {
    parsed = new URLSearchParams(window.location.search);
  } catch {
    parsed = null;
  }

  const fromUrl: UtmData = {};
  if (parsed) {
    const source = parsed.get('utm_source');
    const medium = parsed.get('utm_medium');
    const campaign = parsed.get('utm_campaign');
    const term = parsed.get('utm_term');
    const content = parsed.get('utm_content');
    if (source) fromUrl.source = source;
    if (medium) fromUrl.medium = medium;
    if (campaign) fromUrl.campaign = campaign;
    if (term) fromUrl.term = term;
    if (content) fromUrl.content = content;
  }

  const referrer = hasDocument() ? document.referrer || undefined : undefined;
  if (referrer) fromUrl.referrer = referrer;

  const hasAnyUtm =
    fromUrl.source !== undefined ||
    fromUrl.medium !== undefined ||
    fromUrl.campaign !== undefined ||
    fromUrl.term !== undefined ||
    fromUrl.content !== undefined;

  if (hasAnyUtm) {
    try {
      window.localStorage.setItem(UTM_KEY, JSON.stringify(fromUrl));
    } catch {
      // ignore quota / privacy-mode errors
    }
    return fromUrl;
  }

  // No UTM in URL → return existing stored UTM if any
  try {
    const stored = window.localStorage.getItem(UTM_KEY);
    if (!stored) return null;
    const decoded = JSON.parse(stored) as unknown;
    if (decoded && typeof decoded === 'object') {
      return decoded as UtmData;
    }
    return null;
  } catch {
    return null;
  }
}

function getStoredUtm(): UtmData | null {
  if (!hasLocalStorage()) return null;
  try {
    const stored = window.localStorage.getItem(UTM_KEY);
    if (!stored) return null;
    const decoded = JSON.parse(stored) as unknown;
    if (decoded && typeof decoded === 'object') {
      return decoded as UtmData;
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Lang helper ─────────────────────────────────────────────────────

function getLang(): string {
  if (!hasLocalStorage()) return 'no';
  try {
    const raw = window.localStorage.getItem(LANG_KEY);
    if (!raw) return 'no';
    // Zustand persist stores JSON like {"state":{"lang":"no"},"version":0}
    const decoded = JSON.parse(raw) as unknown;
    if (decoded && typeof decoded === 'object') {
      const state = (decoded as { state?: unknown }).state;
      if (state && typeof state === 'object') {
        const lang = (state as { lang?: unknown }).lang;
        if (typeof lang === 'string') return lang;
      }
      // Fallback: a bare {lang: 'no'} shape
      const flatLang = (decoded as { lang?: unknown }).lang;
      if (typeof flatLang === 'string') return flatLang;
    }
    if (typeof decoded === 'string') return decoded;
    return 'no';
  } catch {
    return 'no';
  }
}

// ─── Core API ────────────────────────────────────────────────────────

/**
 * One-time setup. Safe to call multiple times — subsequent calls are no-ops.
 * - Ensures anonymous ID exists
 * - Captures UTM from current URL (if any)
 * - Subscribes to a global `share-click` CustomEvent and forwards its detail
 *   payload via trackEvent('share_click', detail)
 */
export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  if (!hasWindow()) return;

  // Warm identity + UTM
  getAnonId();
  getSessionId();
  captureUtm();

  // Global share-click listener — ShareButtons component can dispatch a
  // CustomEvent('share-click', { detail: { channel, url } }) and this
  // module will forward it as a tracked event. No per-component wiring needed.
  window.addEventListener('share-click', (e: Event) => {
    const detail =
      (e as CustomEvent<Record<string, unknown> | undefined>).detail ?? {};
    trackEvent('share_click', detail);
  });

  // Best-effort flush on page hide so we don't lose tail-end events
  window.addEventListener('pagehide', () => {
    void flush();
  });
  window.addEventListener('beforeunload', () => {
    void flush();
  });
}

/**
 * Tracks a page view. Call this from a route-change effect.
 * Properties: { path, title? }
 */
export function trackPageView(path: string, title?: string): void {
  const props: Record<string, unknown> = { path };
  if (title !== undefined) props.title = title;
  trackEvent('page_view', props);
  // GA4 needs a dedicated page_view shape (page_path / page_title /
  // page_location) — trackEvent already forwards generic events, but this
  // call uses GA4's canonical page-view payload for clean Acquisition reports.
  sendPageViewToGA4(path, title);
}

/**
 * Tracks a named event with arbitrary properties.
 *
 * Per-event property shapes:
 *   page_view:           { path: string, title?: string }
 *   signup_start:        { method: 'google' | 'email' }
 *   signup_complete:     { method: 'google' | 'email', plan?: 'free' | 'paid' }
 *   lesson_preview_open: { subject: string, lessonId?: string }
 *   interactive_engaged: { lessonId: string, kind: 'game' | 'embed' | 'try', subject: string }
 *   email_capture:       { source: 'blog' | 'sticky' | 'undring-listing', slug?: string }
 *   share_click:         { channel: 'whatsapp' | 'facebook' | 'messenger' | 'copy' | 'threads', url?: string }
 *   cta_click:           { cta: string, location: string }
 *   subscription_start:  { plan?: string, [key: string]: unknown }
 *
 * Never throws. If Firestore isn't configured or write fails, the event is
 * dropped (and in dev, a console.warn is logged).
 */
export function trackEvent(
  name: EventName,
  properties: Record<string, unknown> = {}
): void {
  if (!hasWindow()) return;

  // Always forward to GA4 first (synchronous, fire-and-forget). GA4 reporting
  // works even if Firestore is unconfigured, and vice versa.
  // page_view is sent separately by sendPageViewToGA4 to use GA4's canonical
  // shape — skip the generic forward here to avoid double-counting.
  if (name !== 'page_view') {
    sendToGA4(name, properties);
  }

  try {
    const event: QueuedEvent = {
      eventName: name,
      properties,
      path: hasWindow() ? window.location.pathname : '',
      referrer: hasDocument() ? document.referrer : '',
      utm: getStoredUtm(),
      anonId: getAnonId(),
      sessionId: getSessionId(),
      userId: getCurrentUserId(),
      ts: serverTimestamp(),
      lang: getLang(),
      viewport: {
        w: hasWindow() ? window.innerWidth : 0,
        h: hasWindow() ? window.innerHeight : 0,
      },
      ua:
        typeof navigator !== 'undefined'
          ? navigator.userAgent.substring(0, 200)
          : '',
    };

    queue.push(event);

    if (queue.length >= FLUSH_MAX_QUEUE) {
      void flush();
      return;
    }

    if (flushTimer === null) {
      flushTimer = setTimeout(() => {
        void flush();
      }, FLUSH_INTERVAL_MS);
    }
  } catch (err) {
    if (isDev()) {
       
      console.warn('[analytics] trackEvent failed silently', err);
    }
  }
}

// ─── Internal: user ID & flush ───────────────────────────────────────

function getCurrentUserId(): string | null {
  if (!isFirebaseConfigured()) return null;
  try {
    return auth?.currentUser?.uid ?? null;
  } catch {
    return null;
  }
}

async function flush(): Promise<void> {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const batchEvents = queue;
  queue = [];

  if (!isFirebaseConfigured()) {
    if (isDev()) {
       
      console.warn(
        '[analytics] Firebase not configured — dropping events',
        batchEvents.length
      );
    }
    return;
  }

  try {
    const col = collection(db, 'analytics_events');
    const batch = writeBatch(db);
    for (const evt of batchEvents) {
      // Create a new doc ref with auto-generated ID via collection ref
      const ref = doc(col);
      batch.set(ref, evt);
    }
    await batch.commit();
  } catch (err) {
    if (isDev()) {
       
      console.warn('[analytics] flush failed silently', err);
    }
    // Failsafe: swallow. Events are already dequeued; we do NOT retry to
    // avoid runaway loops on permission errors.
  }
}

// === WIRING GUIDE — for the CEO to apply ===
//
// These snippets show exactly where to call the analytics functions. Paste
// them into the indicated files. NO changes to this module are required.
//
// ---------------------------------------------------------------
// 1. src/main.tsx — initialise once at app boot (before render)
// ---------------------------------------------------------------
//
//   import { initAnalytics } from './lib/analytics';
//   initAnalytics();
//
//   // ...existing ReactDOM.createRoot(...).render(...)
//
//
// ---------------------------------------------------------------
// 2. src/App.tsx — track every route change
// ---------------------------------------------------------------
//
//   import { usePageView } from './hooks/usePageView';
//
//   function PageViewTracker() {
//     usePageView();
//     return null;
//   }
//
//   // ...inside <BrowserRouter> (must be a child of the Router so
//   // useLocation works), render <PageViewTracker /> once:
//   //
//   //   <BrowserRouter>
//   //     <PageViewTracker />
//   //     <Routes>...</Routes>
//   //   </BrowserRouter>
//
//
// ---------------------------------------------------------------
// 3. src/pages/Login.tsx — signup funnel
// ---------------------------------------------------------------
//
//   import { trackEvent } from '../lib/analytics';
//
//   // On Google button click handler:
//   trackEvent('signup_start', { method: 'google' });
//   // ...await signInWithPopup / Redirect...
//   trackEvent('signup_complete', { method: 'google', plan: 'free' });
//
//   // On email submit handler:
//   trackEvent('signup_start', { method: 'email' });
//   // ...await createUserWithEmailAndPassword...
//   trackEvent('signup_complete', { method: 'email', plan: 'free' });
//
//
// ---------------------------------------------------------------
// 4. src/pages/Landing.tsx — CTA clicks
// ---------------------------------------------------------------
//
//   import { trackEvent } from '../lib/analytics';
//
//   // On the primary navbar CTA:
//   onClick={() => {
//     trackEvent('cta_click', { cta: 'kom_i_gang', location: 'navbar' });
//     // ...existing navigation
//   }}
//
//   // On the hero CTA:
//   onClick={() => {
//     trackEvent('cta_click', { cta: 'kom_i_gang', location: 'hero' });
//     // ...existing navigation
//   }}
//
//
// ---------------------------------------------------------------
// 5. src/components/blog/EmailCapture.tsx — after successful Firestore write
// ---------------------------------------------------------------
//
//   import { trackEvent } from '../../lib/analytics';
//
//   // After `await addDoc(collection(db, 'email_signups'), {...})`:
//   trackEvent('email_capture', { source: 'blog', slug });
//   // For other sources use: source: 'sticky' | 'undring-listing'
//
//
// ---------------------------------------------------------------
// 6. src/components/blog/ShareButtons.tsx — NO WIRING NEEDED
// ---------------------------------------------------------------
//
//   initAnalytics() subscribes to a global 'share-click' CustomEvent. Have
//   the share button handler dispatch:
//
//     window.dispatchEvent(new CustomEvent('share-click', {
//       detail: { channel: 'whatsapp', url: shareUrl }
//     }));
//
//   The analytics module catches it and writes a `share_click` event
//   automatically. No import of this module from ShareButtons required.
//
// ============================================================
